import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { useConfigQuery } from 'utils/api/useConfigQuery';
import { replaceUrl } from 'utils/helpers/replaceUrl';

export const DEFAULT_FUND_ID = 6;
export const DEFAULT_FUND_NAME = 'YouVersion';

export interface Fund {
  id: number;
  name: string;
}

let hasSetIncomingFund = false;

export function useFund(language?: string) {
  const { data } = useConfigQuery(language);
  const { t } = useTranslation('common');

  if (data?.funds && !hasSetIncomingFund) {
    hasSetIncomingFund = true;
    void setFundFromIncomingUrl(data.funds as Array<Fund>);
  }

  // This is a hotfix to address an issue where there is a hidden fund that givers can give to.
  // As a temporary workaround, we are appending this fund until we develop a long term solution
  // where the API may provide these hidden funds in another way.
  const fundNames = useMemo(() => {
    return (data?.funds ?? []).concat([{ id: 12, name: t('giving.titles.bible_app_lite') }]);
  }, [data?.funds, t]);

  return {
    funds: data?.funds ?? [],
    getFundName: useMemo(() => getFundName(fundNames), [fundNames]),
    selectedFund: getSelectedFundId(),
    setFund,
  };
}

function getFundName(funds: Array<Fund>) {
  return function (id: number) {
    return funds.find(f => f.id === id)?.name ?? DEFAULT_FUND_NAME;
  };
}

function getSelectedFundId() {
  if (typeof window === 'undefined') {
    return DEFAULT_FUND_ID;
  }
  return parseInt(new URLSearchParams(location.search).get('fund') ?? DEFAULT_FUND_ID.toString());
}

async function setFund(fund: number) {
  return await replaceUrl({ fund });
}

async function setFundFromIncomingUrl(funds: Array<Fund>) {
  const fundParam = new URLSearchParams(location.search).get('fund');
  const fund = funds.find(f => f.name === fundParam);
  // IF we can find the fund by name, switch it to be an id.
  if (!fund) {
    return;
  }
  void replaceUrl({ fund: fund.id });
}
